/**
 * Phase F11 FE-3 — proctor review panel.
 *
 * Top-level component the IA enquiry detail page mounts inside its
 * "Proctoring" section. Renders one tab per attempt that has an
 * active proctor profile, plus an artifact gallery + event timeline
 * for the currently-selected attempt.
 *
 * Hidden states:
 *   * Enquiry has no attempts → returns ``null`` (the parent decides
 *     whether to render the section header at all).
 *   * Enquiry has attempts but none have a non-``off`` proctor
 *     profile → render an explainer card so the IA isn't left
 *     wondering why no gallery shows.
 *   * Loading / error states have explicit copy and don't fall back
 *     to skeletons (Iron Rule #16 §16: skeletons are for tabular
 *     loading; this surface uses explicit "loading…" copy because the
 *     gallery is a card grid, not a table).
 *
 * The panel does not auto-refetch on focus; the underlying queries
 * have a 30 s ``staleTime``. F11.5 will add a refresh button + an
 * auto-refresh affordance once we have webhooks to invalidate from.
 */
import * as React from "react";
import { Loader2 } from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Badge,
} from "@eduviaos/ui-kit";
import { useEnquiryAttempts, useProctorArtifacts, useProctorEvents } from "@/hooks/useAdmissions";
import { ProctorArtifactGallery } from "@/components/proctor/ProctorArtifactGallery";
import { ProctorEventTimeline } from "@/components/proctor/ProctorEventTimeline";
import { CheatAssessmentSummary } from "@/components/proctor/CheatAssessmentSummary";
import { GrantReattemptButton } from "@/components/proctor/GrantReattemptDialog";

interface ProctorReviewPanelProps {
  enquiryId: string;
}

export function ProctorReviewPanel({ enquiryId }: ProctorReviewPanelProps) {
  const attemptsQuery = useEnquiryAttempts(enquiryId);
  // useMemo so the array reference is stable when ``items`` is the
  // same — otherwise a parent re-render forces every memoised hook
  // downstream to recompute, including the proctored filter below.
  const allAttempts = React.useMemo(
    () => attemptsQuery.data?.items ?? [],
    [attemptsQuery.data?.items],
  );
  const proctoredAttempts = React.useMemo(
    () => allAttempts.filter((a) => isProctorActive(a.proctor_profile)),
    [allAttempts],
  );
  const [activeAttemptId, setActiveAttemptId] = React.useState<string | null>(null);

  // Default-select the most recent proctored attempt as soon as the
  // list lands. Re-running this on every attempts change would clobber
  // the IA's active selection — only run it when the current selection
  // becomes invalid (e.g. attempts changed and the prior pick is gone).
  React.useEffect(() => {
    if (proctoredAttempts.length === 0) {
      if (activeAttemptId !== null) setActiveAttemptId(null);
      return;
    }
    const stillExists = proctoredAttempts.some((a) => a.id === activeAttemptId);
    if (!stillExists) {
      setActiveAttemptId(proctoredAttempts[0]!.id);
    }
  }, [proctoredAttempts, activeAttemptId]);

  if (attemptsQuery.isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Proctoring review</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-muted-foreground flex items-center gap-2 py-6 text-sm">
            <Loader2 className="text-primary h-4 w-4 animate-spin" />
            <span>Loading attempts…</span>
          </div>
        </CardContent>
      </Card>
    );
  }
  if (attemptsQuery.error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Proctoring review</CardTitle>
        </CardHeader>
        <CardContent>
          <p role="alert" className="text-destructive py-4 text-sm">
            We couldn't load this enquiry's assessment attempts. Please refresh.
          </p>
        </CardContent>
      </Card>
    );
  }

  if (allAttempts.length === 0) {
    // Don't render anything — the parent's section header already
    // communicates "no proctored runs" via its own empty-state copy.
    return null;
  }

  if (proctoredAttempts.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Proctoring review</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground py-4 text-sm">
            None of this enquiry's {allAttempts.length} attempt
            {allAttempts.length === 1 ? "" : "s"} ran with proctoring enabled, so there is nothing
            to review here.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Proctoring review</CardTitle>
      </CardHeader>
      <CardContent>
        <Tabs
          value={activeAttemptId ?? proctoredAttempts[0]!.id}
          onValueChange={setActiveAttemptId}
        >
          <TabsList className="mb-4 flex flex-wrap">
            {proctoredAttempts.map((a) => (
              <TabsTrigger key={a.id} value={a.id} className="gap-2">
                <span>{shortAttemptLabel(a.template_slug, a.assigned_at)}</span>
                <Badge variant="secondary" className="text-[10px] uppercase">
                  {a.status}
                </Badge>
              </TabsTrigger>
            ))}
          </TabsList>
          {proctoredAttempts.map((a) => (
            <TabsContent key={a.id} value={a.id}>
              <ProctorAttemptDetail
                attemptId={a.id}
                enquiryId={enquiryId}
                attemptStatus={a.status}
                templateSlug={a.template_slug}
              />
            </TabsContent>
          ))}
        </Tabs>
      </CardContent>
    </Card>
  );
}

interface ProctorAttemptDetailProps {
  attemptId: string;
  enquiryId: string;
  attemptStatus: string;
  templateSlug: string;
}

function ProctorAttemptDetail({
  attemptId,
  enquiryId,
  attemptStatus,
  templateSlug,
}: ProctorAttemptDetailProps) {
  const artifactsQuery = useProctorArtifacts(attemptId, "all");
  const eventsQuery = useProctorEvents(attemptId, "all");

  return (
    <div className="space-y-6">
      {/* Phase F8b BE-1 FE — admin grant-reattempt CTA.
          Sits at the top of the per-attempt detail because granting
          a re-attempt is the highest-impact action the IA can take
          here (it invalidates the current attempt + assigns a fresh
          one + notifies the candidate). The button is disabled with
          an explanatory tooltip when the attempt is in a terminal
          status (`submitted` / `graded` / `expired` / `withdrawn`). */}
      <div className="flex flex-wrap items-center justify-end gap-2">
        <GrantReattemptButton
          attemptId={attemptId}
          enquiryId={enquiryId}
          attemptStatus={attemptStatus}
          templateSlug={templateSlug}
        />
      </div>

      {/* Phase F12 FE-1 — cheat-detection verdict.
          Sits above the artifact / event surfaces because the
          severity + signal breakdown is the answer to "should I
          escalate this attempt?" — the gallery and timeline are
          the supporting evidence the IA reaches for after reading
          the verdict. */}
      <section aria-label="Cheat-detection assessment">
        <h3 className="mb-3 text-sm font-semibold">Cheat-detection assessment</h3>
        <CheatAssessmentSummary attemptId={attemptId} />
      </section>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[2fr_1fr]">
        <section aria-label="Captured artifacts">
          <h3 className="mb-3 text-sm font-semibold">Captured artifacts</h3>
          {artifactsQuery.isLoading ? (
            <p className="text-muted-foreground text-sm">Loading artifacts…</p>
          ) : artifactsQuery.error ? (
            <p role="alert" className="text-destructive text-sm">
              Couldn't load artifacts.
            </p>
          ) : (
            <ProctorArtifactGallery
              attemptId={attemptId}
              artifacts={artifactsQuery.data?.items ?? []}
            />
          )}
        </section>
        <section aria-label="Event timeline">
          <h3 className="mb-3 text-sm font-semibold">Event timeline</h3>
          {eventsQuery.isLoading ? (
            <p className="text-muted-foreground text-sm">Loading events…</p>
          ) : eventsQuery.error ? (
            <p role="alert" className="text-destructive text-sm">
              Couldn't load events.
            </p>
          ) : (
            <ProctorEventTimeline events={eventsQuery.data?.items ?? []} />
          )}
        </section>
      </div>
    </div>
  );
}

function isProctorActive(profile: string | null | undefined): boolean {
  if (!profile) return false;
  const v = profile.trim().toLowerCase();
  return v !== "" && v !== "off" && v !== "none";
}

function shortAttemptLabel(templateSlug: string, assignedAt: string): string {
  const d = new Date(assignedAt);
  const date = d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  return `${templateSlug} · ${date}`;
}
